import { useState, useEffect, useContext, useRef } from 'react';
import AppContext from '../../../context';
import Data from '../../../repository';
import Loading from '../../components/Loading';
import { ChromePicker } from 'react-color';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import styles from '../../../styles/Admin.module.scss';

const defaultPaymentState = {
	paymentProcessor: '',
	worldpayTestMode: true,
	worldpayEntity: '',
	worldpayUsername: '',
	worldpayPassword: '',
	stripeTestMode: true,
	stripeSecretKey: '',
	stripeEndpointSecret: '',
	sumupTestMode: true,
	sumupClientId: '',
	sumupClientSecret: '',
	sumupMerchantId: '',
	sumupPayToEmail: '',
	googlePayEnabled: false,
	googlePayMerchantId: '',
	applePayDomainAssociation: ''
};

const defaultBusinessState = {
	name: '',
	requireActivation: false,
	healthScreeningEnabled: false,
	pinCodeSuffix: '',
	pinAffixPosition: 'suffix',
	videoInduction: true,
	inductionVideoId: '',
	walletEnabled: false,
	walletLowBalanceThreshold: 0,
	walletLowBalanceRemindersEnabled: true,
	openGymTokenCost: 1,
	classTokenCost: 2,
	cancellationWindowHours: 24,
	paidExtraCancellationWindowHours: 48,
	notifyAdminOnSessionBooking: false,
	primaryColor: '',
	navBarColor: '',
	navBarActiveColor: '',
	terms: '',
	termsDocumentName: '',
	logo: '',
	metaPixelEnabled: false,
	metaPixelId: '',
	metaConversionsAccessToken: '',
	metaTestEventCode: '',
	metaCurrency: 'GBP',
	remoteLockClientId: '',
	remoteLockSecret: ''
};

function Settings() {
	const context = useContext(AppContext);
	const [activeTab, setActiveTab] = useState('profile');
	const [loading, setLoading] = useState({
		page: true,
		profile: false,
		password: false,
		payment: false,
		business: false,
		email: false
	});
	const [errors, setErrors] = useState({
		profile: null,
		password: null,
		payment: null,
		business: null,
		email: null
	});
	const [success, setSuccess] = useState({
		profile: false,
		password: false,
		payment: false,
		business: false,
		email: false
	});
	const [profile, setProfile] = useState({
		firstName: '',
		lastName: '',
		phoneNumber: ''
	});
	const [password, setPassword] = useState({
		currentPassword: '',
		newPassword: '',
		confirmPassword: ''
	});
	const [payment, setPayment] = useState(defaultPaymentState);
	const [business, setBusiness] = useState(defaultBusinessState);
	const [processorLocked, setProcessorLocked] = useState(true);
	const [emailAdmins, setEmailAdmins] = useState([]);
	const [emailTemplates, setEmailTemplates] = useState([]);
	const [emailRecipients, setEmailRecipients] = useState({});
	const [remoteLockTesting, setRemoteLockTesting] = useState(false);
	const [remoteLockTestResult, setRemoteLockTestResult] = useState(null);
	const [logoFile, setLogoFile] = useState(null);
	const [logoUploading, setLogoUploading] = useState(false);
	const logoInputRef = useRef(null);
	const [paidExtrasPlan, setPaidExtrasPlan] = useState({ _id: null, amount: '', interval_count: 1, interval: 'month', paymentType: 'subscription' });
	const [paidExtrasLoading, setPaidExtrasLoading] = useState(false);
	const [paidExtrasError, setPaidExtrasError] = useState(null);
	const [paidExtrasSuccess, setPaidExtrasSuccess] = useState(false);
	const isVariableMode = !['sessions', 'membership', 'experiences', 'hybrid', 'hybrid-new'].includes(context.domain?.businessType);
	const isSessionsMode = context.domain?.businessType === 'sessions';
	const cancellationWindowApplicable = ['sessions', 'hybrid', 'hybrid-new'].includes(context.domain?.businessType);
	const remoteLockApplicable = ['sessions', 'hybrid', 'hybrid-new'].includes(context.domain?.businessType);

	useEffect(() => {
		if (context.user) {
			setProfile({
				firstName: context.user.firstName || '',
				lastName: context.user.lastName || '',
				phoneNumber: context.user.phoneNumber || ''
			});
		}
	}, [context.user]);

	useEffect(() => {
		const loadPaymentSettings = async () => {
			try {
				const result = await Data.fetchDomainAdmin();
				const domain = result.data || {};
				const processor = domain.paymentProcessor || '';
				setPayment({
					paymentProcessor: processor,
					worldpayTestMode: domain.worldpayTestMode ?? true,
					worldpayEntity: domain.worldpayEntity || '',
					worldpayUsername: domain.worldpayUsername || '',
					worldpayPassword: domain.worldpayPassword || '',
					stripeTestMode: domain.stripeTestMode ?? true,
					stripeSecretKey: domain.stripeSecretKey || '',
					stripeEndpointSecret: domain.stripeEndpointSecret || '',
					sumupTestMode: domain.sumupTestMode ?? true,
					sumupClientId: domain.sumupClientId || '',
					sumupClientSecret: domain.sumupClientSecret || '',
					sumupMerchantId: domain.sumupMerchantId || '',
					sumupPayToEmail: domain.sumupPayToEmail || '',
					googlePayEnabled: domain.googlePayEnabled ?? false,
					googlePayMerchantId: domain.googlePayMerchantId || '',
					applePayDomainAssociation: domain.applePayDomainAssociation || ''
				});
				setBusiness({
					name: domain.name || '',
					requireActivation: domain.requireActivation ?? false,
					healthScreeningEnabled: domain.healthScreeningEnabled ?? false,
					pinCodeSuffix: domain.pinCodeSuffix || '',
					pinAffixPosition: domain.pinAffixPosition || 'suffix',
					videoInduction: domain.videoInduction ?? true,
					inductionVideoId: domain.inductionVideoId || '',
					walletEnabled: domain.walletEnabled ?? false,
					walletLowBalanceThreshold: domain.walletLowBalanceThreshold ?? 0,
					walletLowBalanceRemindersEnabled: domain.walletLowBalanceRemindersEnabled ?? true,
					openGymTokenCost: domain.openGymTokenCost ?? 1,
					classTokenCost: domain.classTokenCost ?? 2,
					cancellationWindowHours: domain.cancellationWindowHours ?? 24,
				paidExtraCancellationWindowHours: domain.paidExtraCancellationWindowHours ?? 48,
					notifyAdminOnSessionBooking: domain.notifyAdminOnSessionBooking ?? false,
					primaryColor: domain.primaryColor || '',
					navBarColor: domain.navBarColor || '',
					navBarActiveColor: domain.navBarActiveColor || '',
					terms: domain.terms || '',
					termsDocumentName: domain.termsDocumentName || '',
					logo: domain.logo || '',
					metaPixelEnabled: domain.metaPixelEnabled ?? false,
					metaPixelId: domain.metaPixelId || '',
					metaConversionsAccessToken: domain.metaConversionsAccessToken || '',
					metaTestEventCode: domain.metaTestEventCode || '',
					metaCurrency: domain.metaCurrency || 'GBP',
					remoteLockClientId: domain.remoteLockClientId || '',
					remoteLockSecret: domain.remoteLockSecret || ''
				});
				setProcessorLocked(!!processor);
				setErrors((prev) => ({ ...prev, payment: null }));
			} catch (err) {
				setErrors((prev) => ({ ...prev, payment: 'Failed to load payment settings.' }));
			} finally {
				setLoading((prev) => ({ ...prev, page: false }));
			}
		};

		loadPaymentSettings();
	}, []);

	useEffect(() => {
		if (!context.domain?.paidExtrasAvailable) return;
		const loadPaidExtrasPlan = async () => {
			try {
				const result = await Data.fetchPaidExtrasPlan();
				const plan = result.data;
				if (plan) {
					setPaidExtrasPlan({
						_id: plan._id,
						amount: plan.amount != null ? (plan.amount / 100).toFixed(2) : '',
						interval_count: plan.interval_count ?? 1,
						interval: plan.interval || 'month',
						paymentType: plan.paymentType || 'subscription'
					});
				}
			} catch (err) {
				setPaidExtrasError('Failed to load Paid Extras plan.');
			}
		};
		loadPaidExtrasPlan();
	}, [context.domain?.paidExtrasAvailable]);

	const submitPaidExtrasPlan = async (event) => {
		event.preventDefault();
		setPaidExtrasError(null);
		setPaidExtrasSuccess(false);
		setPaidExtrasLoading(true);
		try {
			const payload = {
				name: 'Paid Extras',
				description: 'Unlocks the waitlist, extended booking window, personalized PIN and longer cancellation window.',
				amount: Math.round(Number(paidExtrasPlan.amount || 0) * 100),
				active: true,
				isPublic: true,
				interval_count: Number(paidExtrasPlan.interval_count || 1),
				interval: paidExtrasPlan.interval,
				paymentType: paidExtrasPlan.paymentType,
				isPaidExtrasPlan: true
			};
			if (paidExtrasPlan._id) {
				await Data.updateMembershipPlan({ _id: paidExtrasPlan._id, ...payload });
			} else {
				const result = await Data.createMembershipPlan(payload);
				setPaidExtrasPlan((prev) => ({ ...prev, _id: result.data._id }));
			}
			setPaidExtrasSuccess(true);
			setTimeout(() => setPaidExtrasSuccess(false), 3000);
		} catch (err) {
			setPaidExtrasError(err.response?.data?.message || 'Failed to save Paid Extras plan.');
		} finally {
			setPaidExtrasLoading(false);
		}
	}

	useEffect(() => {
		const loadEmailSettings = async () => {
			try {
				const result = await Data.fetchEmailNotificationSettings();
				const templates = result.data?.templates || [];
				setEmailAdmins(result.data?.admins || []);
				setEmailTemplates(templates);
				setEmailRecipients(
					templates.reduce((acc, template) => {
						acc[template.key] = template.recipientIds || [];
						return acc;
					}, {})
				);
			} catch (err) {
				setErrors((prev) => ({ ...prev, email: 'Failed to load email notification settings.' }));
			}
		};

		loadEmailSettings();
	}, []);

	const triggerSuccess = (key) => {
		setSuccess((prev) => ({ ...prev, [key]: true }));
		setTimeout(() => {
			setSuccess((prev) => ({ ...prev, [key]: false }));
		}, 3000);
	};

	const handleProfileSubmit = async (event) => {
		event.preventDefault();
		setLoading((prev) => ({ ...prev, profile: true }));
		setErrors((prev) => ({ ...prev, profile: null }));

		try {
			await Data.updateUser(profile);
			await context.fetchUser();
			triggerSuccess('profile');
		} catch (err) {
			setErrors((prev) => ({ ...prev, profile: 'Failed to update profile.' }));
		} finally {
			setLoading((prev) => ({ ...prev, profile: false }));
		}
	};

	const handlePasswordSubmit = async (event) => {
		event.preventDefault();
		setLoading((prev) => ({ ...prev, password: true }));
		setErrors((prev) => ({ ...prev, password: null }));

		if (!password.currentPassword || !password.newPassword) {
			setErrors((prev) => ({ ...prev, password: 'Please enter your current and new password.' }));
			setLoading((prev) => ({ ...prev, password: false }));
			return;
		}

		if (password.newPassword !== password.confirmPassword) {
			setErrors((prev) => ({ ...prev, password: 'New password and confirmation do not match.' }));
			setLoading((prev) => ({ ...prev, password: false }));
			return;
		}

		try {
			await Data.changePassword({
				Domain: context.domain?._id,
				email: context.user?.email,
				oldPassword: password.currentPassword,
				newPassword: password.newPassword
			});
			setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
			triggerSuccess('password');
		} catch (err) {
			setErrors((prev) => ({ ...prev, password: err.response?.data?.message || 'Failed to update password.' }));
		} finally {
			setLoading((prev) => ({ ...prev, password: false }));
		}
	};

	const handlePaymentSubmit = async (event) => {
		event.preventDefault();
		setLoading((prev) => ({ ...prev, payment: true }));
		setErrors((prev) => ({ ...prev, payment: null }));

		try {
			const updatePayload = {
				worldpayTestMode: payment.worldpayTestMode,
				worldpayEntity: payment.worldpayEntity,
				worldpayUsername: payment.worldpayUsername,
				worldpayPassword: payment.worldpayPassword,
				stripeTestMode: payment.stripeTestMode,
				stripeSecretKey: payment.stripeSecretKey,
				stripeEndpointSecret: payment.stripeEndpointSecret,
				sumupTestMode: payment.sumupTestMode,
				sumupClientId: payment.sumupClientId,
				sumupClientSecret: payment.sumupClientSecret,
				sumupMerchantId: payment.sumupMerchantId,
				sumupPayToEmail: payment.sumupPayToEmail,
				googlePayEnabled: payment.googlePayEnabled,
				googlePayMerchantId: payment.googlePayMerchantId,
				applePayDomainAssociation: payment.applePayDomainAssociation
			};

			if (!processorLocked && payment.paymentProcessor) {
				updatePayload.paymentProcessor = payment.paymentProcessor;
			}

			await Data.updateDomain(updatePayload);
			await context.fetchDomain();
			if (!processorLocked && payment.paymentProcessor) {
				setProcessorLocked(true);
			}
			triggerSuccess('payment');
		} catch (err) {
			setErrors((prev) => ({ ...prev, payment: 'Failed to update payment settings.' }));
		} finally {
			setLoading((prev) => ({ ...prev, payment: false }));
		}
	};

	const handleLogoUpload = async () => {
		if (!logoFile) return;
		setLogoUploading(true);
		setErrors((prev) => ({ ...prev, business: null }));

		try {
			const formData = new FormData();
			formData.append('image', logoFile);
			const response = await Data.uploadImage(formData);
			const logoUrl = response.data.data.Location;
			setBusiness((prev) => ({ ...prev, logo: logoUrl }));
			await Data.updateDomain({ logo: logoUrl });
			await context.fetchDomain();
			setLogoFile(null);
			if (logoInputRef.current) logoInputRef.current.value = '';
		} catch (err) {
			setErrors((prev) => ({ ...prev, business: 'Failed to upload logo.' }));
		} finally {
			setLogoUploading(false);
		}
	};

	const handleBusinessSubmit = async (event) => {
		event.preventDefault();
		setLoading((prev) => ({ ...prev, business: true }));
		setErrors((prev) => ({ ...prev, business: null }));

		try {
			await Data.updateDomain({
				...(isVariableMode ? { name: business.name } : {}),
				requireActivation: business.requireActivation,
				healthScreeningEnabled: business.healthScreeningEnabled,
				...(isSessionsMode ? { pinCodeSuffix: business.pinCodeSuffix, pinAffixPosition: business.pinAffixPosition } : {}),
				videoInduction: business.videoInduction,
				inductionVideoId: business.inductionVideoId,
				walletEnabled: business.walletEnabled,
				walletLowBalanceThreshold: Number(business.walletLowBalanceThreshold || 0),
				walletLowBalanceRemindersEnabled: business.walletLowBalanceRemindersEnabled,
				openGymTokenCost: Number(business.openGymTokenCost || 0),
				classTokenCost: Number(business.classTokenCost || 0),
				...(cancellationWindowApplicable ? { cancellationWindowHours: Number(business.cancellationWindowHours) } : {}),
			...(context.domain?.paidExtrasAvailable ? { paidExtraCancellationWindowHours: Number(business.paidExtraCancellationWindowHours) } : {}),
				...(cancellationWindowApplicable ? { notifyAdminOnSessionBooking: business.notifyAdminOnSessionBooking } : {}),
				primaryColor: business.primaryColor,
				navBarColor: business.navBarColor,
				navBarActiveColor: business.navBarActiveColor,
				terms: business.terms,
				termsDocumentName: business.termsDocumentName,
				logo: business.logo,
				metaPixelEnabled: business.metaPixelEnabled,
				metaPixelId: business.metaPixelId,
				metaConversionsAccessToken: business.metaConversionsAccessToken,
				metaTestEventCode: business.metaTestEventCode,
				metaCurrency: business.metaCurrency || 'GBP',
				...(remoteLockApplicable ? {
					remoteLockClientId: business.remoteLockClientId,
					remoteLockSecret: business.remoteLockSecret
				} : {})
			});
			await context.fetchDomain();
			triggerSuccess('business');
		} catch (err) {
			setErrors((prev) => ({ ...prev, business: 'Failed to update business settings.' }));
		} finally {
			setLoading((prev) => ({ ...prev, business: false }));
		}
	};

	const handleRemoteLockTestConnection = async () => {
		setRemoteLockTesting(true);
		setRemoteLockTestResult(null);

		try {
			const result = await Data.testRemoteLockConnection({
				remoteLockClientId: business.remoteLockClientId,
				remoteLockSecret: business.remoteLockSecret
			});
			setRemoteLockTestResult({ success: true, message: result.data?.message || 'Successfully connected to RemoteLock.' });
		} catch (err) {
			setRemoteLockTestResult({ success: false, message: err.response?.data?.message || 'Failed to connect to RemoteLock.' });
		} finally {
			setRemoteLockTesting(false);
		}
	};

	const toggleEmailRecipient = (templateKey, adminId) => {
		setEmailRecipients((prev) => {
			const current = prev[templateKey] || [];
			const next = current.includes(adminId)
				? current.filter((id) => id !== adminId)
				: [...current, adminId];
			return { ...prev, [templateKey]: next };
		});
	};

	const handleEmailSubmit = async (event) => {
		event.preventDefault();
		setLoading((prev) => ({ ...prev, email: true }));
		setErrors((prev) => ({ ...prev, email: null }));

		try {
			const result = await Data.updateEmailNotificationSettings(emailRecipients);
			const templates = result.data?.templates || [];
			setEmailTemplates(templates);
			setEmailRecipients(
				templates.reduce((acc, template) => {
					acc[template.key] = template.recipientIds || [];
					return acc;
				}, {})
			);
			triggerSuccess('email');
		} catch (err) {
			setErrors((prev) => ({ ...prev, email: 'Failed to update email notification settings.' }));
		} finally {
			setLoading((prev) => ({ ...prev, email: false }));
		}
	};

	if (loading.page) return <Loading button={false} />;

	return (
		<div className={styles.fullWidth}>
			<div className={styles.tabs}>
				<button
					type="button"
					className={`${styles.tab} ${activeTab === 'profile' ? styles.active : ''}`}
					onClick={() => setActiveTab('profile')}
				>
					Profile
				</button>
				<button
					type="button"
					className={`${styles.tab} ${activeTab === 'password' ? styles.active : ''}`}
					onClick={() => setActiveTab('password')}
				>
					Password
				</button>
				<button
					type="button"
					className={`${styles.tab} ${activeTab === 'payment' ? styles.active : ''}`}
					onClick={() => setActiveTab('payment')}
				>
					Payment Gateway
				</button>
				<button
					type="button"
					className={`${styles.tab} ${activeTab === 'business' ? styles.active : ''}`}
					onClick={() => setActiveTab('business')}
				>
					Business
				</button>
				<button
					type="button"
					className={`${styles.tab} ${activeTab === 'email' ? styles.active : ''}`}
					onClick={() => setActiveTab('email')}
				>
					Emails
				</button>
			</div>

			<div className={styles.tabContent}>
				{activeTab === 'profile' && (
					<form className={styles.form} onSubmit={handleProfileSubmit}>
						<div>
							<p>First Name:</p>
							<input
								type="text"
								value={profile.firstName}
								onChange={(event) => setProfile({ ...profile, firstName: event.target.value })}
							/>
						</div>
						<div>
							<p>Last Name:</p>
							<input
								type="text"
								value={profile.lastName}
								onChange={(event) => setProfile({ ...profile, lastName: event.target.value })}
							/>
						</div>
						<div>
							<p>Phone Number:</p>
							<input
								type="text"
								value={profile.phoneNumber}
								onChange={(event) => setProfile({ ...profile, phoneNumber: event.target.value })}
							/>
						</div>

						{errors.profile && <p style={{ color: 'red' }}>{errors.profile}</p>}
						{success.profile && <p style={{ color: 'green' }}>Profile updated successfully.</p>}

						<button className="btn btnPrimary" disabled={loading.profile}>
							{loading.profile ? <Loading button={true} /> : 'Save Profile'}
						</button>
					</form>
				)}

				{activeTab === 'password' && (
					<form className={styles.form} onSubmit={handlePasswordSubmit}>
						<div>
							<p>Current Password:</p>
							<input
								type="password"
								value={password.currentPassword}
								onChange={(event) => setPassword({ ...password, currentPassword: event.target.value })}
							/>
						</div>
						<div>
							<p>New Password:</p>
							<input
								type="password"
								value={password.newPassword}
								onChange={(event) => setPassword({ ...password, newPassword: event.target.value })}
							/>
						</div>
						<div>
							<p>Confirm New Password:</p>
							<input
								type="password"
								value={password.confirmPassword}
								onChange={(event) => setPassword({ ...password, confirmPassword: event.target.value })}
							/>
						</div>

						{errors.password && <p style={{ color: 'red' }}>{errors.password}</p>}
						{success.password && <p style={{ color: 'green' }}>Password updated successfully.</p>}

						<button className="btn btnPrimary" disabled={loading.password}>
							{loading.password ? <Loading button={true} /> : 'Update Password'}
						</button>
					</form>
				)}

				{activeTab === 'payment' && (
					<form className={styles.form} onSubmit={handlePaymentSubmit}>
						{processorLocked ? (
							<div>
								<p>Payment Processor:</p>
								<p>{payment.paymentProcessor}</p>
							</div>
						) : (
							<div>
								<p>Payment Processor:</p>
								<select
									value={payment.paymentProcessor}
									onChange={(event) => setPayment({ ...payment, paymentProcessor: event.target.value })}
								>
									<option value="">Select a processor</option>
									<option value="Worldpay">Worldpay</option>
									<option value="Stripe">Stripe</option>
									<option value="SumUp">SumUp</option>
								</select>
							</div>
						)}

						{payment.paymentProcessor === 'Worldpay' && (
							<>
								<div>
									<p>Worldpay Test Mode:</p>
									<select
										value={payment.worldpayTestMode ? 'true' : 'false'}
										onChange={(event) => setPayment({ ...payment, worldpayTestMode: event.target.value === 'true' })}
									>
										<option value="true">Enabled</option>
										<option value="false">Disabled</option>
									</select>
								</div>
								<div>
									<p>Worldpay Entity:</p>
									<input
										type="text"
										value={payment.worldpayEntity}
										onChange={(event) => setPayment({ ...payment, worldpayEntity: event.target.value })}
									/>
								</div>
								<div>
									<p>Worldpay Username:</p>
									<input
										type="text"
										value={payment.worldpayUsername}
										onChange={(event) => setPayment({ ...payment, worldpayUsername: event.target.value })}
									/>
								</div>
								<div>
									<p>Worldpay Password:</p>
									<input
										type="password"
										value={payment.worldpayPassword}
										onChange={(event) => setPayment({ ...payment, worldpayPassword: event.target.value })}
									/>
								</div>
							</>
						)}

						{payment.paymentProcessor === 'Stripe' && (
							<>
								<div>
									<p>Stripe Test Mode:</p>
									<select
										value={payment.stripeTestMode ? 'true' : 'false'}
										onChange={(event) => setPayment({ ...payment, stripeTestMode: event.target.value === 'true' })}
									>
										<option value="true">Enabled</option>
										<option value="false">Disabled</option>
									</select>
								</div>
								<div>
									<p>Stripe Secret Key:</p>
									<input
										type="password"
										value={payment.stripeSecretKey}
										onChange={(event) => setPayment({ ...payment, stripeSecretKey: event.target.value })}
									/>
								</div>
								<div>
									<p>Stripe Endpoint Secret:</p>
									<input
										type="password"
										value={payment.stripeEndpointSecret}
										onChange={(event) => setPayment({ ...payment, stripeEndpointSecret: event.target.value })}
									/>
								</div>
							</>
						)}

						{payment.paymentProcessor === 'SumUp' && (
							<>
								<div>
									<p>SumUp Test Mode:</p>
									<select
										value={payment.sumupTestMode ? 'true' : 'false'}
										onChange={(event) => setPayment({ ...payment, sumupTestMode: event.target.value === 'true' })}
									>
										<option value="true">Enabled</option>
										<option value="false">Disabled</option>
									</select>
								</div>
								<div>
									<p>SumUp Client ID:</p>
									<input
										type="text"
										value={payment.sumupClientId}
										onChange={(event) => setPayment({ ...payment, sumupClientId: event.target.value })}
									/>
								</div>
								<div>
									<p>SumUp Client Secret:</p>
									<input
										type="password"
										value={payment.sumupClientSecret}
										onChange={(event) => setPayment({ ...payment, sumupClientSecret: event.target.value })}
									/>
								</div>
								<div>
									<p>SumUp Merchant ID:</p>
									<input
										type="text"
										value={payment.sumupMerchantId}
										onChange={(event) => setPayment({ ...payment, sumupMerchantId: event.target.value })}
									/>
								</div>
								<div>
									<p>SumUp Pay To Email:</p>
									<input
										type="email"
										value={payment.sumupPayToEmail}
										onChange={(event) => setPayment({ ...payment, sumupPayToEmail: event.target.value })}
									/>
								</div>

								<div>
									<p>Apple Pay Domain Association File:</p>
									<textarea
										rows={4}
										placeholder={"Paste the contents of this domain's apple-developer-merchantid-domain-association file, issued by SumUp for this exact hostname."}
										value={payment.applePayDomainAssociation}
										onChange={(event) => setPayment({ ...payment, applePayDomainAssociation: event.target.value })}
									/>
								</div>

								<div>
									<p>Google Pay:</p>
									<select
										value={payment.googlePayEnabled ? 'true' : 'false'}
										onChange={(event) => setPayment({ ...payment, googlePayEnabled: event.target.value === 'true' })}
									>
										<option value="false">Disabled</option>
										<option value="true">Enabled</option>
									</select>
								</div>
								{payment.googlePayEnabled && (
									<div>
										<p>Google Pay Merchant ID:</p>
										<input
											type="text"
											value={payment.googlePayMerchantId}
											onChange={(event) => setPayment({ ...payment, googlePayMerchantId: event.target.value })}
										/>
									</div>
								)}
							</>
						)}

						{errors.payment && <p style={{ color: 'red' }}>{errors.payment}</p>}
						{success.payment && <p style={{ color: 'green' }}>Payment settings updated successfully.</p>}

						<button className="btn btnPrimary" disabled={loading.payment}>
							{loading.payment ? <Loading button={true} /> : 'Save Payment Settings'}
						</button>
					</form>
				)}

				{activeTab === 'business' && (
					<form className={styles.form} onSubmit={handleBusinessSubmit}>
						{isVariableMode && (
							<div>
								<p>Site Name:</p>
								<input
									type="text"
									value={business.name}
									onChange={(event) => setBusiness({ ...business, name: event.target.value })}
								/>
							</div>
						)}
						<div>
							<p>Require Activation Before Booking:</p>
							<select
								value={business.requireActivation ? 'true' : 'false'}
								onChange={(event) => setBusiness({ ...business, requireActivation: event.target.value === 'true' })}
							>
								<option value="true">Enabled</option>
								<option value="false">Disabled</option>
							</select>
						</div>

						{!isVariableMode && context.domain?.businessType !== 'experiences' && (
							<div>
								<p>Enable Health Screening Form:</p>
								<select
									value={business.healthScreeningEnabled ? 'true' : 'false'}
									onChange={(event) => setBusiness({ ...business, healthScreeningEnabled: event.target.value === 'true' })}
								>
									<option value="true">Enabled</option>
									<option value="false">Disabled</option>
								</select>
							</div>
						)}

						{isSessionsMode && (
							<>
								<div>
									<p>PIN Prefix/Suffix ("#"):</p>
									<div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
										{[
											{ value: 'prefix', label: 'Prefix (e.g. #1234)' },
											{ value: 'suffix', label: 'Suffix (e.g. 1234#)' },
											{ value: 'none', label: 'None' }
										].map(opt => (
											<label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
												<input
													type="radio"
													name="pinAffixPosition"
													value={opt.value}
													checked={(business.pinCodeSuffix ? business.pinAffixPosition : 'none') === opt.value}
													onChange={() => setBusiness(opt.value === 'none'
														? { ...business, pinCodeSuffix: '' }
														: { ...business, pinCodeSuffix: '#', pinAffixPosition: opt.value })}
													style={{ margin: 0, width: 'auto' }}
												/>
												{opt.label}
											</label>
										))}
									</div>
									<span className={styles.helpText}>Added to the access PIN shown to customers - e.g. on a keypad lock that needs # pressed to confirm entry.</span>
								</div>
							</>
						)}

						<div>
							<p>Enable Wallet:</p>
							<select
								value={business.walletEnabled ? 'true' : 'false'}
								onChange={(event) => setBusiness({ ...business, walletEnabled: event.target.value === 'true' })}
							>
								<option value="true">Enabled</option>
								<option value="false">Disabled</option>
							</select>
						</div>

						<div>
							<p>Low Balance Threshold - in pence:</p>
							<input
								type="number"
								value={business.walletLowBalanceThreshold}
								onChange={(event) => setBusiness({ ...business, walletLowBalanceThreshold: event.target.value })}
							/>
						</div>

						<div>
							<p>Low Balance Reminders:</p>
							<select
								value={business.walletLowBalanceRemindersEnabled ? 'true' : 'false'}
								onChange={(event) => setBusiness({ ...business, walletLowBalanceRemindersEnabled: event.target.value === 'true' })}
							>
								<option value="true">Enabled</option>
								<option value="false">Disabled</option>
							</select>
						</div>

						<div>
							<p>Enable Meta Pixel:</p>
							<select
								value={business.metaPixelEnabled ? 'true' : 'false'}
								onChange={(event) => setBusiness({ ...business, metaPixelEnabled: event.target.value === 'true' })}
							>
								<option value="false">Disabled</option>
								<option value="true">Enabled</option>
							</select>
						</div>

						<div>
							<p>Meta Pixel ID:</p>
							<input
								type="text"
								value={business.metaPixelId}
								onChange={(event) => setBusiness({ ...business, metaPixelId: event.target.value })}
							/>
						</div>

						<div>
							<p>Conversions API Token:</p>
							<input
								type="password"
								value={business.metaConversionsAccessToken}
								onChange={(event) => setBusiness({ ...business, metaConversionsAccessToken: event.target.value })}
							/>
						</div>

						<div>
							<p>Test Event Code:</p>
							<input
								type="text"
								value={business.metaTestEventCode}
								onChange={(event) => setBusiness({ ...business, metaTestEventCode: event.target.value })}
							/>
						</div>

						<div>
							<p>Meta Currency:</p>
							<input
								type="text"
								maxLength="3"
								value={business.metaCurrency}
								onChange={(event) => setBusiness({ ...business, metaCurrency: event.target.value.toUpperCase() })}
							/>
						</div>

						<div>
							<p>Open Gym Token Cost:</p>
							<input
								type="number"
								min="0"
								value={business.openGymTokenCost}
								onChange={(event) => setBusiness({ ...business, openGymTokenCost: event.target.value })}
							/>
						</div>

						<div>
							<p>Class Token Cost:</p>
							<input
								type="number"
								min="0"
								value={business.classTokenCost}
								onChange={(event) => setBusiness({ ...business, classTokenCost: event.target.value })}
							/>
						</div>

						{cancellationWindowApplicable && (
							<div>
								<p>Cancellation Window (hours):</p>
								<input
									type="number"
									min="0"
									value={business.cancellationWindowHours}
									onChange={(event) => setBusiness({ ...business, cancellationWindowHours: event.target.value })}
								/>
							</div>
						)}

						{context.domain?.paidExtrasAvailable && (
							<div>
								<p>Paid Extras Cancellation Window (hours):</p>
								<input
									type="number"
									min="0"
									value={business.paidExtraCancellationWindowHours}
									onChange={(event) => setBusiness({ ...business, paidExtraCancellationWindowHours: event.target.value })}
								/>
								<p className={"text-sm text-gray-600"}>Applies instead of the window above for customers who've purchased Paid Extras.</p>
							</div>
						)}

						{cancellationWindowApplicable && (
							<div>
								<p>Booking Email Notifications:</p>
								<div className={"flex items-center gap-2"}>
									<input
										type="checkbox"
										checked={business.notifyAdminOnSessionBooking}
										onChange={(event) => setBusiness({ ...business, notifyAdminOnSessionBooking: event.target.checked })}
									/>
									<span className={"text-sm text-gray-600"}>
										Send a copy of the booking confirmation email to your admin email address whenever a customer books a session.
									</span>
								</div>
							</div>
						)}


								<div>
									<p>Primary Color:</p>
									<ChromePicker
										color={business.primaryColor}
										onChange={(color) => setBusiness({ ...business, primaryColor: color.hex })}
									/>
								</div>
								<div>
									<p>Nav Bar Color:</p>
									<ChromePicker
										color={business.navBarColor}
										onChange={(color) => setBusiness({ ...business, navBarColor: color.hex })}
									/>
								</div>
								<div>
									<p>Nav Bar Active Color:</p>
									<ChromePicker
										color={business.navBarActiveColor}
										onChange={(color) => setBusiness({ ...business, navBarActiveColor: color.hex })}
									/>
								</div>
								<div>
									<p>Enable Induction Video:</p>
									<select
										value={business.videoInduction ? 'true' : 'false'}
										onChange={(event) => setBusiness({ ...business, videoInduction: event.target.value === 'true' })}
									>
										<option value="true">Enabled</option>
										<option value="false">Disabled</option>
									</select>
								</div>
								{business.videoInduction && (
									<div>
										<p>Induction Video (YouTube Video ID):</p>
										<input
											type="text"
											placeholder="e.g. YurQPfmAGfg"
											value={business.inductionVideoId}
											onChange={(event) => setBusiness({ ...business, inductionVideoId: event.target.value })}
										/>
									</div>
								)}
								<div>
									<p>Terms Document Name:</p>
									<input
										type="text"
										value={business.termsDocumentName}
										onChange={(event) => setBusiness({ ...business, termsDocumentName: event.target.value })}
									/>
								</div>
								<div>
									<p>Terms:</p>
									<ReactQuill
										theme="snow"
										value={business.terms}
										onChange={(value) => setBusiness({ ...business, terms: value })}
									/>
								</div>
								<div>
									<p>Logo:</p>
									{business.logo && (
										<img src={business.logo} alt="Logo" style={{ width: '80px', height: '80px', objectFit: 'contain', marginBottom: '8px' }} />
									)}
									<div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
										<input
											ref={logoInputRef}
											type="file"
											accept="image/*"
											onChange={(event) => setLogoFile(event.target.files[0])}
											style={{ flex: 'none', maxWidth: '100%' }}
										/>
										<button
											type="button"
											className="btn btnPrimary"
											disabled={!logoFile || logoUploading}
											onClick={handleLogoUpload}
											style={{ padding: '8px 16px', width: 'auto' }}
										>
											{logoUploading ? <Loading button={true} /> : 'Upload Logo'}
										</button>
									</div>
								</div>
							

						{remoteLockApplicable && (
							<>
								<p style={{ fontWeight: 'bold', marginTop: '12px' }}>RemoteLock Access Control</p>

								<div>
									<p>RemoteLock Client ID:</p>
									<input
										type="text"
										value={business.remoteLockClientId}
										onChange={(event) => setBusiness({ ...business, remoteLockClientId: event.target.value })}
									/>
								</div>
								<div>
									<p>RemoteLock Secret:</p>
									<input
										type="password"
										value={business.remoteLockSecret}
										onChange={(event) => setBusiness({ ...business, remoteLockSecret: event.target.value })}
									/>
								</div>
								<div>
									<p>Connection:</p>
									<div>
										<button
											type="button"
											className="btn btnEmpty"
											disabled={remoteLockTesting || !business.remoteLockClientId || !business.remoteLockSecret}
											onClick={handleRemoteLockTestConnection}
											style={{ padding: '8px 16px', width: 'auto' }}
										>
											{remoteLockTesting ? <Loading button={true} /> : 'Test Connection'}
										</button>
										{remoteLockTestResult && (
											<p style={{ color: remoteLockTestResult.success ? 'green' : 'red', marginTop: '8px' }}>
												{remoteLockTestResult.message}
											</p>
										)}
									</div>
								</div>
							</>
						)}

						{errors.business && <p style={{ color: 'red' }}>{errors.business}</p>}
						{success.business && <p style={{ color: 'green' }}>Business settings updated successfully.</p>}

						<button className="btn btnPrimary" disabled={loading.business}>
							{loading.business ? <Loading button={true} /> : 'Save Business Settings'}
						</button>
					</form>
				)}

				{activeTab === 'business' && context.domain?.paidExtrasAvailable && (
					<form className={styles.form} onSubmit={submitPaidExtrasPlan}>
						<h3 style={{ margin: 0 }}>Paid Extras</h3>
						<p className={"text-sm text-gray-600"} style={{ marginTop: '-10px' }}>
							Configure what your customers pay to unlock the waitlist, extended booking window, personalized PIN and longer cancellation window.
						</p>

						<div>
							<p>Price (£):</p>
							<input
								type="number"
								min="0"
								step="0.01"
								value={paidExtrasPlan.amount}
								onChange={(event) => setPaidExtrasPlan({ ...paidExtrasPlan, amount: event.target.value })}
								required
							/>
						</div>

						<div>
							<p>Billing Type:</p>
							<select
								value={paidExtrasPlan.paymentType}
								onChange={(event) => setPaidExtrasPlan({ ...paidExtrasPlan, paymentType: event.target.value })}
							>
								<option value="subscription">Recurring subscription</option>
								<option value="one-time">One-time payment</option>
								<option value="both">Let the customer choose</option>
							</select>
						</div>

						{paidExtrasPlan.paymentType !== 'one-time' && (
							<div>
								<p>Bills every:</p>
								<div className={"flex flex-wrap items-center gap-2"}>
									<input
										type="number"
										min="1"
										style={{ width: '80px' }}
										value={paidExtrasPlan.interval_count}
										onChange={(event) => setPaidExtrasPlan({ ...paidExtrasPlan, interval_count: event.target.value })}
									/>
									<select
										style={{ width: 'auto' }}
										value={paidExtrasPlan.interval}
										onChange={(event) => setPaidExtrasPlan({ ...paidExtrasPlan, interval: event.target.value })}
									>
										<option value="day">Day(s)</option>
										<option value="week">Week(s)</option>
										<option value="month">Month(s)</option>
										<option value="year">Year(s)</option>
									</select>
								</div>
							</div>
						)}

						{paidExtrasError && <p style={{ color: 'red' }}>{paidExtrasError}</p>}
						{paidExtrasSuccess && <p style={{ color: 'green' }}>Paid Extras plan saved.</p>}

						<button className="btn btnPrimary" disabled={paidExtrasLoading}>
							{paidExtrasLoading ? <Loading button={true} /> : paidExtrasPlan._id ? 'Update Paid Extras Plan' : 'Create Paid Extras Plan'}
						</button>
					</form>
				)}
				{activeTab === 'email' && (
					<form className={styles.form} onSubmit={handleEmailSubmit}>
						<p>Choose which admin users receive each notification email. If no admin users are selected for a template, it falls back to the site's admin email.</p>

						{emailAdmins.length === 0 && (
							<p style={{ color: 'red' }}>No admin users found for this site. Add an admin user under Users before assigning recipients here.</p>
						)}

						{emailTemplates.map((template) => (
							<div key={template.key} style={{ display: 'block', border: '1px solid #ddd', borderRadius: '4px', padding: '12px', marginBottom: '12px' }}>
								<p style={{ fontWeight: 'bold', marginBottom: '4px' }}>{template.name}</p>
								<p style={{ color: '#666', marginTop: 0, marginBottom: '8px' }}>{template.description}</p>

								{emailAdmins.map((admin) => (
									<label key={admin._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', width: '100%' }}>
										<input
											type="checkbox"
											checked={(emailRecipients[template.key] || []).includes(admin._id)}
											onChange={() => toggleEmailRecipient(template.key, admin._id)}
										/>
										{admin.firstName} {admin.lastName} ({admin.email})
									</label>
								))}
							</div>
						))}

						{errors.email && <p style={{ color: 'red' }}>{errors.email}</p>}
						{success.email && <p style={{ color: 'green' }}>Email notification settings updated successfully.</p>}

						<button className="btn btnPrimary" disabled={loading.email}>
							{loading.email ? <Loading button={true} /> : 'Save Email Settings'}
						</button>
					</form>
				)}

			</div>
		</div>
	);
}

export default Settings;
